import React from "react";
import { Button } from "@/components/ui/button";
import { ChevronLeft, ChevronRight, ChevronsLeft, ChevronsRight } from "lucide-react";

const DEFAULT_SIZES = [25, 50, 100, 200];

/**
 * Client-side paginator strip. Callers keep the sliced rows themselves via
 * `usePagination` (below). This component just renders controls.
 */
export default function Paginator({
  page,
  pageSize,
  total,
  onPageChange,
  onPageSizeChange,
  sizes = DEFAULT_SIZES,
  testIdPrefix = "paginator",
  className = "",
}) {
  const totalPages = Math.max(1, Math.ceil(total / pageSize));
  const from = total === 0 ? 0 : page * pageSize + 1;
  const to = Math.min(total, (page + 1) * pageSize);
  const canPrev = page > 0;
  const canNext = page < totalPages - 1;

  return (
    <div className={`flex flex-wrap items-center justify-between gap-2 text-xs ${className}`}>
      <div className="flex items-center gap-2 text-muted-foreground font-mono">
        <span>Rows</span>
        <select
          value={pageSize}
          onChange={(e) => onPageSizeChange(Number(e.target.value))}
          className="h-7 px-1.5 rounded border border-border bg-card"
          data-testid={`${testIdPrefix}-size`}
        >
          {sizes.map((s) => <option key={s} value={s}>{s}</option>)}
        </select>
        <span>·</span>
        <span data-testid={`${testIdPrefix}-range`}>{from}–{to} of {total.toLocaleString()}</span>
      </div>
      <div className="flex items-center gap-1">
        <Button size="icon" variant="ghost" className="h-7 w-7" disabled={!canPrev} onClick={() => onPageChange(0)} title="First">
          <ChevronsLeft className="h-3.5 w-3.5" />
        </Button>
        <Button size="icon" variant="ghost" className="h-7 w-7" disabled={!canPrev} onClick={() => onPageChange(page - 1)} title="Prev" data-testid={`${testIdPrefix}-prev`}>
          <ChevronLeft className="h-3.5 w-3.5" />
        </Button>
        <span className="font-mono px-2" data-testid={`${testIdPrefix}-page`}>Page {page + 1} / {totalPages}</span>
        <Button size="icon" variant="ghost" className="h-7 w-7" disabled={!canNext} onClick={() => onPageChange(page + 1)} title="Next" data-testid={`${testIdPrefix}-next`}>
          <ChevronRight className="h-3.5 w-3.5" />
        </Button>
        <Button size="icon" variant="ghost" className="h-7 w-7" disabled={!canNext} onClick={() => onPageChange(totalPages - 1)} title="Last">
          <ChevronsRight className="h-3.5 w-3.5" />
        </Button>
      </div>
    </div>
  );
}

/**
 * Companion hook: slices an array based on page + pageSize + total. Auto-clamps.
 *   const { page, setPage, pageSize, setPageSize, paged, total } = usePagination(rows);
 */
export function usePagination(rows, initialSize = 50) {
  const [page, setPage] = React.useState(0);
  const [pageSize, setPageSize] = React.useState(initialSize);
  const total = rows?.length || 0;
  const totalPages = Math.max(1, Math.ceil(total / pageSize));
  const safePage = Math.min(page, totalPages - 1);
  const paged = React.useMemo(() => {
    if (!rows) return [];
    const start = safePage * pageSize;
    return rows.slice(start, start + pageSize);
  }, [rows, safePage, pageSize]);
  // Reset to first page when the source list length shrinks below current page
  React.useEffect(() => { if (page > totalPages - 1) setPage(0); }, [page, totalPages]);
  return { page: safePage, setPage, pageSize, setPageSize, paged, total };
}
